import React, { useMemo, Fragment, FunctionComponent } from 'react';
import { useRouter } from 'next/router';
import { getTitleForLegacyCloudPage } from '@rbx/docsite-utilities/dist/browser';
import { useTranslation } from '@rbx/intl';
import { MenuItem, Select } from '@rbx/ui';
import AppLayout, { LayoutType, TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppMainNavbar from '@modules/layout/layout/components/AppMainNavbar';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppHeading from '@modules/layout/layout/components/AppHeading';
import AppLeftNav from '@modules/layout/layout/components/AppLeftNav';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import AppRightNavContent, { RenderItem } from '@modules/ui/shared/components/AppRightNavContent';
import SideBarNavigation from '@modules/ui/shared/components/SideBarNavigation';
import stripMarkdown from '@modules/ui/shared/utils/stripMarkdown';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import { TestId } from '@modules/utilities/constants/testConstants';
import EndpointTreeItem from '@modules/ui/shared/components/EndpointTreeItem';
import OpenCloudLegacyAPIContent from '@modules/openCloud/utils/interfaces/OpenCloudLegacyContent';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import CloudIABanner from '@modules/openCloud/CloudIABanner';
import { OpenAPI } from '../../../utils/oasTypes';
import SwaggerDocsContent from '../../../swagger/components/SwaggerDocsContent';

const renderItem: RenderItem = ({ title, label }) => {
  if (typeof label === 'undefined') {
    return <Fragment>{title}</Fragment>;
  }

  const method = OpenAPI.HttpMethods[label.toUpperCase() as keyof typeof OpenAPI.HttpMethods];
  return <EndpointTreeItem method={method} title={title} />;
};

export interface LegacyAPIContainerProps {
  title: string; // unlike content.title, this is specified via docs navigation page title
  content: OpenCloudLegacyAPIContent;
  versions: string[];
  openGraphMeta: Partial<TOpenGraphMeta>;
}

const LegacyAPIContainer: FunctionComponent<React.PropsWithChildren<LegacyAPIContainerProps>> = ({
  title: fallbackTitle,
  content,
  versions,
  openGraphMeta,
}) => {
  const router = useRouter();
  const { translate } = useTranslation();
  const { getBreadcrumbsItems } = useSideBarContent();

  const title = getTitleForLegacyCloudPage(fallbackTitle);

  const breadcrumbsItems = useMemo(() => {
    return getBreadcrumbsItems(translate);
  }, [getBreadcrumbsItems, translate]);

  const { description } = content;

  // const headings = useMemo(() => deriveHeadings(translate, content), [translate, content]);
  const metaDescription = useMemo(
    () => (description != null ? stripMarkdown(description) : null),
    [description],
  );

  return (
    <AppLayout
      testid={TestId.OpenCloudContainer}
      pageTitle={title}
      pageMetaDescription={metaDescription}
      openGraphMeta={openGraphMeta}
      type={LayoutType.OpenCloudV1}
      renderBanner={() => (process.env.enableOpenCloudIAV2Pages ? <CloudIABanner /> : undefined)}>
      <AppMainNavbar pageTitle={title} breadcrumbsItems={breadcrumbsItems} />
      <AppIAHeading breadcrumbsItems={breadcrumbsItems} />
      <AppHeading
        title={title}
        adornment={
          versions.length > 1 && (
            <Select
              value={router.query.version}
              variant='outlined'
              label={translate('Label.Version')}
              margin='dense'>
              {versions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  onClick={() => {
                    router.push({
                      pathname: router.pathname,
                      query: { name: router.query.name, version: option },
                    });
                  }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )
        }
      />
      <AppRightNav content={<AppRightNavContent renderItem={renderItem} allowExpand={false} />} />
      <AppContent>
        <SwaggerDocsContent spec={JSON.stringify(content)} />
      </AppContent>
      <AppLeftNav>
        <SideBarNavigation />
      </AppLeftNav>
    </AppLayout>
  );
};

export default LegacyAPIContainer;
